import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TelegramIcon from "@mui/icons-material/Telegram";
import { useLocation, useNavigate } from "react-router-dom";
import useFetchWithAuth from "../../../features/hooks/useFetchWithAuth";

export default function NowWorks() {
  const { data, loading, error } = useFetchWithAuth("/statistic/workdays/");
  const { data: ratingsData } = useFetchWithAuth(
    "/statistic/operator/ratings/?days=1"
  );
  const [openAll, setOpenAll] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    field: "start",
    direction: null,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 6;

  const operatorsWork = Array.isArray(data?.data?.working)
    ? data.data.working
    : [];
  const ratingsRows = Array.isArray(ratingsData?.data) ? ratingsData.data : [];

  const telegramByUserId = useMemo(() => {
    const map = new Map();
    ratingsRows.forEach((row) => {
      const userId = Number(row?.id);
      const telegram = String(row?.telegram || "").trim();
      if (Number.isFinite(userId) && telegram) {
        map.set(userId, telegram);
      }
    });
    return map;
  }, [ratingsRows]);

  const getShiftTimes = (op) => {
    const workTime = op.work_time || "";
    const [rangeStart, rangeEnd] = workTime.split("-");
    const start =
      op.start ??
      op.start_time ??
      op.time_start ??
      rangeStart ??
      "-";
    const end =
      op.end ??
      op.end_time ??
      op.time_end ??
      rangeEnd ??
      "-";
    return { start, end };
  };

  const parseTimeToMinutes = (value) => {
    const raw = String(value || "").trim();
    const match = raw.match(/^(\d{1,2}):?(\d{2})?$/);
    if (!match) return Number.MAX_SAFE_INTEGER;
    const hours = Number(match[1]);
    const minutes = Number(match[2] || "0");
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return Number.MAX_SAFE_INTEGER;
    }
    return hours * 60 + minutes;
  };

  const getTelegramUsername = (op) => {
    const operatorId = Number(op?.id ?? op?.user_id ?? op?.user?.id ?? op?.user);
    if (Number.isFinite(operatorId) && telegramByUserId.has(operatorId)) {
      const byRating = telegramByUserId.get(operatorId);
      return byRating.replace(/^@/, "").replace(/^https?:\/\/t\.me\//, "");
    }
    const raw = String(
      op?.telegram ??
        op?.telegram_username ??
        op?.tg ??
        op?.user?.telegram ??
        op?.user?.telegram_username ??
        ""
    ).trim();
    if (!raw) return "";
    return raw.replace(/^@/, "").replace(/^https?:\/\/t\.me\//, "");
  };

  const goToOperatorSchedule = (op) => {
    const base = location.pathname.startsWith("/management")
      ? "/management"
      : "/supervisor";
    const label = `${op.first_name || ""} ${op.last_name || ""}`.trim() || op.username || "";
    const operatorId = op.user_id ?? op.user ?? op.id ?? "";
    navigate(
      `${base}/work-schedule/?operator=${encodeURIComponent(
        label
      )}&operator_id=${encodeURIComponent(operatorId)}`
    );
  };

  const toggleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field !== field) return { field, direction: "asc" };
      if (prev.direction === "asc") return { field, direction: "desc" };
      if (prev.direction === "desc") return { field, direction: null };
      return { field, direction: "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.direction) return operatorsWork;
    return [...operatorsWork].sort((a, b) => {
      if (sortConfig.field === "name") {
        const aName = `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.username || "";
        const bName = `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.username || "";
        return sortConfig.direction === "asc"
          ? aName.localeCompare(bName, "uk")
          : bName.localeCompare(aName, "uk");
      }
      const aTime = parseTimeToMinutes(getShiftTimes(a)[sortConfig.field]);
      const bTime = parseTimeToMinutes(getShiftTimes(b)[sortConfig.field]);
      return sortConfig.direction === "asc" ? aTime - bTime : bTime - aTime;
    });
  }, [operatorsWork, sortConfig]);

  const previewRows = useMemo(
    () => sortedRows.slice(0, limit),
    [sortedRows, limit]
  );

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <Box className="rmui-card" sx={{ p: 3, mb: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" className="text-black" fontWeight="bold">
          Сьогодні працюють
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            color: "#6A4FFF",
          }}
          onClick={() => setOpenAll(true)}
        >
          Див. всіх
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ boxShadow: "none" }}
        className="rmui-table border-bottom"
      >
        <Table>
          <TableHead className="bg-f6f7f9">
            <TableRow>
              {[
                { key: "name", label: "Ім’я" },
                { key: "start", label: "Початок" },
                { key: "end", label: "Кінець" },
                { key: "actions", label: "" },
              ].map((col) => (
                <TableCell
                  key={col.key}
                  className="text-black border-bottom"
                  sx={{
                    fontWeight: "500",
                    fontSize: "14px",
                    ...(col.key !== "actions"
                      ? { "&:hover": { backgroundColor: "rgba(96, 93, 255, 0.08)" } }
                      : {}),
                  }}
                  onClick={() => col.key !== "actions" && toggleSort(col.key)}
                  style={col.key !== "actions" ? { cursor: "pointer" } : undefined}
                >
                  {col.label}
                  {col.key !== "actions" && sortConfig.field === col.key
                    ? sortConfig.direction === "asc"
                      ? " ↑"
                      : sortConfig.direction === "desc"
                      ? " ↓"
                      : ""
                    : ""}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Немає даних
                </TableCell>
              </TableRow>
            ) : (
              previewRows.map((op, index) => {
                const fullName = `${op.first_name || ""} ${
                  op.last_name || ""
                }`.trim();
                const { start, end } = getShiftTimes(op);
                const telegram = getTelegramUsername(op);

                return (
                  <TableRow key={op.id || index}>
                    <TableCell className="border-bottom">
                      {fullName || op.username || "-"}
                    </TableCell>
                    <TableCell className="border-bottom">
                      {start}
                    </TableCell>
                    <TableCell className="border-bottom">
                      {end}
                    </TableCell>
                    <TableCell className="border-bottom" align="right">
                      {telegram ? (
                        <IconButton
                          sx={{ color: "#6A4FFF" }}
                          onClick={() =>
                            window.open(`https://t.me/${telegram}`, "_blank")
                          }
                          aria-label="Відкрити Telegram"
                        >
                          <TelegramIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Tooltip title="Telegram не вказаний">
                          <span>
                            <IconButton
                              sx={{ color: "#94A3B8" }}
                              disabled
                              aria-label="Telegram не вказаний"
                            >
                              <TelegramIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <IconButton
                        sx={{ color: "#6A4FFF" }}
                        onClick={() => goToOperatorSchedule(op)}
                      >
                        <CalendarMonthIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openAll} onClose={() => setOpenAll(false)} fullWidth>
        <DialogTitle>Сьогодні працюють</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table size="small">
              <TableHead className="bg-f6f7f9">
                <TableRow>
                  {[
                    { key: "name", label: "Ім’я" },
                    { key: "start", label: "Початок" },
                    { key: "end", label: "Кінець" },
                    { key: "actions", label: "" },
                  ].map((col) => (
                    <TableCell
                      key={col.key}
                      className="text-black border-bottom"
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                        ...(col.key !== "actions"
                          ? {
                              "&:hover": {
                                backgroundColor: "rgba(96, 93, 255, 0.08)",
                              },
                            }
                          : {}),
                      }}
                      onClick={() => col.key !== "actions" && toggleSort(col.key)}
                      style={
                        col.key !== "actions" ? { cursor: "pointer" } : undefined
                      }
                    >
                      {col.label}
                      {col.key !== "actions" && sortConfig.field === col.key
                        ? sortConfig.direction === "asc"
                          ? " ↑"
                          : sortConfig.direction === "desc"
                          ? " ↓"
                          : ""
                        : ""}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {operatorsWork.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Немає даних
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((op, index) => {
                    const fullName = `${op.first_name || ""} ${
                      op.last_name || ""
                    }`.trim();
                    const { start, end } = getShiftTimes(op);
                    const telegram = getTelegramUsername(op);
                    return (
                      <TableRow key={op.id || index}>
                        <TableCell className="border-bottom">
                          {fullName || op.username || "-"}
                        </TableCell>
                        <TableCell className="border-bottom">
                          {start}
                        </TableCell>
                        <TableCell className="border-bottom">{end}</TableCell>
                        <TableCell className="border-bottom" align="right">
                          {telegram ? (
                            <IconButton
                              sx={{ color: "#6A4FFF" }}
                              onClick={() =>
                                window.open(`https://t.me/${telegram}`, "_blank")
                              }
                              aria-label="Відкрити Telegram"
                            >
                              <TelegramIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <Tooltip title="Telegram не вказаний">
                              <span>
                                <IconButton
                                  sx={{ color: "#94A3B8" }}
                                  disabled
                                  aria-label="Telegram не вказаний"
                                >
                                  <TelegramIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <IconButton
                            sx={{ color: "#6A4FFF" }}
                            onClick={() => goToOperatorSchedule(op)}
                          >
                            <CalendarMonthIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAll(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
